import React from "react";
import * as s from "./PostColumn.module.scss";
import {
  Col,
  DynamicImage,
  DynamicLink,
  P,
  ShareResource,
  ModalToolbar,
  Overlay,
} from "resource-centre-theme/src/views/shared/components";
import { CopyLink } from "resource-centre-theme/src/views/shared/modals";
import { inject, observer } from "mobx-react";
import {
  AuthenticationStore,
  UIStore,
  UserStore,
  CacheStore,
  ContextStore,
  UserCollectionStore,
} from "resource-centre-theme/src/stores";
import { truncate } from "resource-centre-theme/src/libs/utils";
import {
  ISingleWordpressArticle,
  ISingleWordpressDocument,
} from "resource-centre-theme/src/models";
import { tx } from "resource-centre-theme/src/libs/i18n";
import { Icons } from "resource-centre-theme/src/libs/assets";

interface Props {
  post: ISingleWordpressDocument | ISingleWordpressArticle;
  userStore?: UserStore;
  authenticationStore?: AuthenticationStore;
  uiStore?: UIStore;
  cacheStore?: CacheStore;
  contextStore?: ContextStore;
  userCollectionStore?: UserCollectionStore;
}
interface State {
  shareOpen: boolean;
  moreOpen: boolean;
  copyOpen: boolean;
}

@inject(
  "userStore",
  "authenticationStore",
  "uiStore",
  "cacheStore",
  "contextStore",
  "userCollectionStore"
)
@observer
export class PostColumn extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = { shareOpen: false, moreOpen: false, copyOpen: false };
  }

  toggleShare = () => {
    this.setState({ shareOpen: !this.state.shareOpen });
  };

  toggleMore = () => {
    this.setState({ moreOpen: !this.state.moreOpen });
  };

  toggleCopy = () => {
    this.setState({ copyOpen: !this.state.copyOpen });
  };

  render() {
    const {
      post,
      uiStore,
      userCollectionStore,
      authenticationStore,
      userStore,
    } = this.props;
    const { shareOpen, moreOpen, copyOpen } = this.state;

    let image = null;
    let title = null;
    let meta = null;
    const isDesktop = uiStore?.isDesktop;
    const moreClasses = [s.showMore];
    const shareClasses = [s.shareOverlay];
    const copyClasses = [s.copyOverlay];
    const { onRemovePostFromCollection } = userCollectionStore;
    const { userHasValidToken } = authenticationStore;
    const { myLibrary } = userStore;
    const documentURL =
      post?.documentFields?.documents?.[0]?.documentFile?.mediaItemUrl;

    if (post.nodeType === "Document") {
      image = post.documentThumbnail?.image;
      title = post.title;

      meta = [
        post.documentFields?.publicationYear,
        post.documentPublishers?.publishers.length > 0 &&
          post.documentPublishers?.publishers?.[0]?.name,
      ]
        .filter(a => !!a)
        .join(" · ");
    } else if (post.nodeType === "Article") {
      image = post?.articleFields?.imageGroup?.image;
      title = post?.articleFields?.title;
    }

    if (shareOpen) {
      shareClasses.push(s.active);
    }
    if (moreOpen) {
      moreClasses.push(s.active);
    }

    if (copyOpen) {
      copyClasses.push(s.active);
    }

    return (
      <Col xs={6} lg={3} className={s.postCardColumn} key={post.databaseId}>
        <DynamicLink to={post.link} className={s.postCardLink}>
          <div className={s.postImage}>
            <DynamicImage image={image} size="mediumLarge" alt="card-image" />
            <div className={s.postImageOverlay}>
              <div className={s.overlayBottomRow}>
                {userHasValidToken && (
                  <div
                    className={s.removeButton}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      onRemovePostFromCollection(myLibrary, post);
                    }}
                  >
                    <img src={Icons.trash_small_white} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </DynamicLink>
        <div className={s.postTitleRow}>
          {title && (
            <DynamicLink to={post.link}>
              <p className={s.postTitle}>
                {truncate(title, isDesktop ? 75 : 20)}
              </p>
            </DynamicLink>
          )}
          <img
            className={s.moreIcon}
            src={Icons.more_large}
            onClick={this.toggleMore}
          />
        </div>
        {meta && (
          <P small className={s.publishingInformationLabel}>
            {meta}
          </P>
        )}
        <div className={moreClasses.join(" ")} ref={this.wrapperRef}>
          <div className={s.titleRow}>
            {tx("misc.moreOptions") + "..."}
            <div className={s.close} onClick={this.toggleMore}>
              <img src={Icons.close_small} />
            </div>
          </div>
          <div
            className={s.moreRow}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              onRemovePostFromCollection(myLibrary, post);
            }}
          >
            <img src={Icons.trash_small} />
            <p className={s.moreText}>{tx("misc.remove")}</p>
          </div>
          <a href={documentURL} download={title}>
            <div className={s.moreRow}>
              <img src={Icons.download_small} />
              <p className={s.moreText}>{tx("misc.download")}</p>
            </div>
          </a>
          <div className={s.moreRow} onClick={this.toggleCopy}>
            <img src={Icons.link_small} />
            <p className={s.moreText}>{tx("misc.copyLink")}</p>
          </div>
          <div className={s.moreRow} onClick={this.toggleShare}>
            <img src={Icons.share_small} />
            <p className={s.moreText}>{tx("misc.share")}</p>
          </div>
          <div className={shareClasses.join(" ")}>
            <Overlay>
              <div className={s.overlayContainer}>
                <ModalToolbar
                  title={tx("misc.share")}
                  onClose={this.toggleShare}
                  button={Icons.filter_small}
                />
                <ShareResource url={post.link} title={title} />
              </div>
            </Overlay>
          </div>
          <div className={copyClasses.join(" ")}>
            <Overlay>
              <div className={s.overlayContainer}>
                <ModalToolbar
                  title={tx("misc.copyLink")}
                  onClose={this.toggleCopy}
                  button={Icons.filter_small}
                />
                <CopyLink link={post.link} />
              </div>
            </Overlay>
          </div>
        </div>
      </Col>
    );
  }
}
